import React, { useState } from 'react';
import { 
  ArrowRight, 
  ArrowLeft, 
  Check, 
  Calendar, 
  MapPin, 
  User,
  MessageCircle
} from 'lucide-react';
import { contractService } from '../services/contractService';
import { notificationService } from '../services/notificationService';
import { whatsappNotificationSettingsService } from '../services/whatsappNotificationSettingsService';
import { Contract, EnderecoViaCEP, FormErrors } from '../types/contract';
import { formatDate } from '../utils/dateUtils';
import { log } from '../utils/logger';
import { validateCPF, validateCNPJ, formatCPF, formatCNPJ, getDocumentLabel, applyDocumentMask, detectDocumentType, limitDocumentInput } from '../utils/documentUtils';

const Formulario: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [contrato, setContrato] = useState<Partial<Contract>>({
    status: 'orcamento',
    tipoDocumento: 'cpf',
    inicioRecreacao: '',
    fimRecreacao: '',
    createdAt: new Date()
  });

  const [contratoSalvo, setContratoSalvo] = useState<Partial<Contract> | null>(null);

  const [errors, setErrors] = useState<Partial<FormErrors>>({});

  // --- Início da Lógica de Autocompletar ---

  const generateRandomData = () => {
    const firstNames = ["Lucas", "Sofia", "Arthur", "Júlia", "Heitor", "Isabella", "Bernardo", "Manuela"];
    const lastNames = ["Silva", "Santos", "Oliveira", "Souza", "Rodrigues", "Ferreira", "Alves", "Pereira"];
    const childNames = ["Miguel", "Alice", "Gael", "Helena", "Theo", "Maria Alice", "Ravi", "Laura"];
    const themes = ["Patrulha Canina", "Frozen", "Mickey Mouse", "Mundo Bita", "Baby Shark", "Super-Heróis"];
    
    const addresses = [
      { endereco: "Rua Professor Juventino Nunes", bairro: "Centro", cidade: "Manhuaçu", estado: "MG", cep: "36900000", numero: "123" },
      { endereco: "Avenida Getúlio Vargas", bairro: "Coqueiro", cidade: "Manhuaçu", estado: "MG", cep: "36900001", numero: "456" },
      { endereco: "Rua Monsenhor Gonzalez", bairro: "Centro", cidade: "Manhumirim", estado: "MG", cep: "36970000", numero: "789" },
      { endereco: "Praça Cordovil Pinto Coelho", bairro: "Centro", cidade: "Manhuaçu", estado: "MG", cep: "36900035", numero: "101" },
      { endereco: "Rua Coronel José Pedro", bairro: "Centro", cidade: "Simonésia", estado: "MG", cep: "36930000", numero: "212" }
    ];

    const randomFromArray = (arr: any[]) => arr[Math.floor(Math.random() * arr.length)];

    const generateCPF = () => {
      const rnd = (n: number) => Math.round(Math.random() * n);
      const n = Array(9).fill(0).map(() => rnd(9));
      let d1 = n.map((v, i) => v * (10 - i)).reduce((acc, v) => acc + v, 0) % 11;
      d1 = d1 < 2 ? 0 : 11 - d1;
      let d2 = [...n, d1].map((v, i) => v * (11 - i)).reduce((acc, v) => acc + v, 0) % 11;
      d2 = d2 < 2 ? 0 : 11 - d2;
      return `${n.join('')}${d1}${d2}`;
    };

    const contratante = `${randomFromArray(firstNames)} ${randomFromArray(lastNames)}`;
    const randomAddress = randomFromArray(addresses);
    const futureDate = new Date();
    futureDate.setMonth(futureDate.getMonth() + Math.floor(Math.random() * 6) + 1);
    futureDate.setDate(Math.floor(Math.random() * 28) + 1);

    const generatedData: Partial<Contract> = {
      // Step 1
      contratante: contratante,
      cpf: generateCPF(),
      telefone: `339${Math.floor(10000000 + Math.random() * 90000000)}`.slice(0, 11),
      email: `${contratante.toLowerCase().replace(/\s/g, '.')}@example.com`,
      // Step 2
      nomeAniversariante: randomFromArray(childNames),
      idadeAniversariante: Math.floor(Math.random() * 10) + 1,
      dataFesta: futureDate.toISOString().split('T')[0],
      horarioInicioFesta: `${Math.floor(Math.random() * 6) + 14}:00`,
      inicioRecreacao: `${Math.floor(Math.random() * 6) + 14}:00`,
      fimRecreacao: `${Math.floor(Math.random() * 4) + 18}:00`,
      temaFesta: randomFromArray(themes),
      numeroConvidados: Math.floor(Math.random() * 81) + 20,
      // Step 3
      ...randomAddress,
      status: 'orcamento',
      createdAt: new Date()
    };
    
    setContrato(generatedData);
    setErrors({});
    setCurrentStep(4);
  };

  const handleContratanteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.toLowerCase() === 'autocompletar') {
      generateRandomData();
    } else {
      setContrato(prev => ({ ...prev, contratante: value }));
    }
  };

  // --- Fim da Lógica de Autocompletar ---

  const validateCPF = (cpf: string): boolean => {
    const cleanCPF = cpf.replace(/\D/g, '');
    if (cleanCPF.length !== 11) return false;
    if (/^(\d)\1+$/.test(cleanCPF)) return false;
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
    }
    let remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cleanCPF.charAt(9))) return false;
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cleanCPF.charAt(10))) return false;
    return true;
  };

  const validatePhone = (phone: string): boolean => {
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone.length >= 10 && cleanPhone.length <= 11;
  };

  const buscarCEP = async (cep: string) => {
    if (cep.length !== 8) return;
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data: EnderecoViaCEP = await response.json();
      if (!data.erro) {
        setContrato(prev => ({
          ...prev,
          endereco: data.logradouro,
          bairro: data.bairro,
          cidade: data.localidade,
          estado: data.uf
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    }
  };

  const validateStep = (): boolean => {
    const newErrors: Partial<FormErrors> = {};
    switch (currentStep) {
      case 1:
        if (!contrato.contratante?.trim()) newErrors.contratante = 'Nome é obrigatório';
        
        // Validação do documento baseada no tipo selecionado
        if (contrato.tipoDocumento === 'cpf') {
          if (!contrato.cpf?.trim()) {
            newErrors.cpf = 'CPF é obrigatório';
          } else if (!validateCPF(contrato.cpf)) {
            newErrors.cpf = 'CPF inválido';
          }
        } else if (contrato.tipoDocumento === 'cnpj') {
          if (!contrato.cnpj?.trim()) {
            newErrors.cnpj = 'CNPJ é obrigatório';
          } else if (!validateCNPJ(contrato.cnpj)) {
            newErrors.cnpj = 'CNPJ inválido';
          }
        }
        
        if (!contrato.telefone?.trim()) {
          newErrors.telefone = 'Telefone é obrigatório';
        } else if (!validatePhone(contrato.telefone)) {
          newErrors.telefone = 'Telefone inválido';
        }
        break;
      case 2:
        if (!contrato.nomeAniversariante?.trim()) newErrors.nomeAniversariante = 'Nome do aniversariante é obrigatório';
        if (!contrato.idadeAniversariante) newErrors.idadeAniversariante = 'Idade é obrigatória';
        if (!contrato.dataFesta) newErrors.dataFesta = 'Data da festa é obrigatória';
        if (!contrato.horarioInicioFesta?.trim()) newErrors.horarioInicioFesta = 'Horário é obrigatório';
        if (!contrato.inicioRecreacao?.trim()) newErrors.inicioRecreacao = 'Horário de início da recreação é obrigatório';
        if (!contrato.fimRecreacao?.trim()) newErrors.fimRecreacao = 'Horário de fim da recreação é obrigatório';
        break;
      case 3:
        if (!contrato.endereco?.trim()) newErrors.endereco = 'Endereço é obrigatório';
        if (!contrato.numero?.trim()) newErrors.numero = 'Número é obrigatório';
        if (!contrato.bairro?.trim()) newErrors.bairro = 'Bairro é obrigatório';
        if (!contrato.cidade?.trim()) newErrors.cidade = 'Cidade é obrigatória';
        if (!contrato.estado?.trim()) newErrors.estado = 'Estado é obrigatório';
        break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    
    log.info('Iniciando envio de formulário de contrato', 'Formulario.tsx', {
      contratante: contrato.contratante,
      telefone: contrato.telefone,
      dataFesta: contrato.dataFesta
    });
    
    try {
      const contractId = await contractService.saveContract(contrato as Contract);
      log.info('Contrato salvo com sucesso', 'Formulario.tsx', { contractId });
      
      await contractService.updateContract(contractId, { id: contractId });
      log.info('Contrato atualizado com ID', 'Formulario.tsx', { contractId });
      
      // Enviar notificação automática via WhatsApp
      log.info('Enviando notificação de novo contrato', 'Formulario.tsx', { contractId });
      await notificationService.sendNewContractNotification({
        ...contrato,
        id: contractId
      } as Contract);

      try {
        const result = await whatsappNotificationSettingsService.sendContractNotificationIfEnabled({
          ...contrato,
          id: contractId
        } as Contract);
        log.info('Envio automatico WhatsApp processado', 'Formulario.tsx', result);
      } catch (whatsappError) {
        log.error('Erro ao enviar WhatsApp automatico', 'Formulario.tsx', whatsappError);
        console.error('Erro ao enviar WhatsApp automatico:', whatsappError);
      }
      
      setContratoSalvo(contrato); // Salvar dados do contrato antes de limpar
      setSuccess(true);
      setContrato({ status: 'orcamento', createdAt: new Date() });
      setCurrentStep(1);
      
      log.info('Formulário enviado com sucesso', 'Formulario.tsx', { contractId });
    } catch (error) {
      log.error('Erro ao enviar formulário', 'Formulario.tsx', error);
      console.error('Erro ao enviar formulário:', error);
      alert('Erro ao enviar sua solicitação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppRedirect = () => {
    if (!contratoSalvo) return;
    
    const nomeContratante = contratoSalvo.contratante || 'Não informado';
    const local = contratoSalvo.endereco && contratoSalvo.numero && contratoSalvo.cidade && contratoSalvo.estado 
      ? `${contratoSalvo.endereco}, ${contratoSalvo.numero}, ${contratoSalvo.cidade} - ${contratoSalvo.estado}`
      : 'Não informado';
    const data = contratoSalvo.dataFesta ? formatDate(contratoSalvo.dataFesta) : 'Não informada';
    const hora = contratoSalvo.horarioInicioFesta || 'Não informada';
    
    const message = `Olá! Acabei de preencher o formulário de contrato da PlayKids e gostaria de mais informações sobre os serviços.

📋 *Dados do Contrato:*
👤 *Nome:* ${nomeContratante}
📍 *Local:* ${local}
📅 *Data:* ${data}
🕐 *Horário:* ${hora}

Aguardo retorno!`;

    const phoneNumber = "5533998204921";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <Check className="h-6 w-6 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Contrato realizado com sucesso!</h2>
          <p className="text-gray-600 mb-4">
            Sua solicitação foi enviada e você receberá seu contrato via Whatsapp em breve.
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
            <div className="flex items-center gap-2 text-green-800 text-sm">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="font-medium">Notificação enviada para nossa equipe!</span>
            </div>
            <p className="text-green-700 text-xs mt-1">
              Nossa equipe foi notificada automaticamente e entrará em contato em breve.
            </p>
          </div>
          <div className="space-y-3">
            <button
              onClick={handleWhatsAppRedirect}
              className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
            >
              <MessageCircle className="w-5 h-5" />
              Falar no WhatsApp
            </button>
            <button
              onClick={() => setSuccess(false)}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Nova Solicitação
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-slate-50 to-blue-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-12">
          <div className="mb-6">
            <img src="/images/LOGO.png" alt="PlayKids Logo" className="w-24 h-auto mx-auto" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3 font-display">Contratar</h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Preencha os dados para iniciar a contratação
          </p>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 p-4 sm:p-8 mb-8">
          <div className="flex items-center justify-between mb-4">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-all duration-300 ${
                  currentStep >= step 
                    ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-lg scale-110' 
                    : 'bg-slate-200 text-slate-600'
                }`}>
                  {currentStep > step ? <Check className="w-5 h-5" /> : step}
                </div>
                {step < 4 && (
                  <div className={`hidden sm:block w-12 sm:w-20 h-1 mx-3 rounded-full transition-all duration-300 ${
                    currentStep > step 
                      ? 'bg-gradient-to-r from-teal-500 to-teal-600' 
                      : 'bg-slate-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="hidden sm:flex flex-col sm:flex-row justify-between text-sm font-medium text-slate-600 gap-2">
            <span className="text-center flex-1">Seus Dados</span>
            <span className="text-center flex-1">Evento</span>
            <span className="text-center flex-1">Local</span>
            <span className="text-center flex-1">Confirmação</span>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 p-4 sm:p-8">
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-lg sm:text-2xl font-bold text-slate-900 flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                Seus Dados
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">Nome Completo *</label>
                  <input type="text" value={contrato.contratante || ''} onChange={handleContratanteChange} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 ${errors.contratante ? 'border-red-500 bg-red-50' : 'border-slate-300 bg-white/50'}`} placeholder="Nome Completo *"/>
                  {errors.contratante && <p className="text-red-500 text-sm mt-1">{errors.contratante}</p>}
                </div>
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">Tipo de Documento *</label>
                  <select 
                    value={contrato.tipoDocumento || 'cpf'} 
                    onChange={(e) => {
                      setContrato(prev => ({ 
                        ...prev, 
                        tipoDocumento: e.target.value as 'cpf' | 'cnpj'
                      }));
                    }} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="cpf">CPF</option>
                    <option value="cnpj">CNPJ</option>
                  </select>
                </div>
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">{getDocumentLabel(contrato.tipoDocumento || 'cpf')} *</label>
                  <input 
                    type="text" 
                    value={contrato.tipoDocumento === 'cnpj' ? (contrato.cnpj || '') : (contrato.cpf || '')} 
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      const cleanValue = limitDocumentInput(rawValue, contrato.tipoDocumento || 'cpf');
                      const formattedValue = applyDocumentMask(cleanValue, contrato.tipoDocumento || 'cpf');
                      
                      // Apenas formatação baseada no tipo selecionado
                      if (contrato.tipoDocumento === 'cnpj') {
                        setContrato(prev => ({ ...prev, cnpj: formattedValue }));
                      } else {
                        setContrato(prev => ({ ...prev, cpf: formattedValue }));
                      }
                    }} 
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${(errors.cpf || errors.cnpj) ? 'border-red-500' : 'border-gray-300'}`} 
                    placeholder={`${getDocumentLabel(contrato.tipoDocumento || 'cpf')} *`}
                    maxLength={contrato.tipoDocumento === 'cnpj' ? 18 : 14} // 18 para CNPJ (14 dígitos + 4 caracteres), 14 para CPF (11 dígitos + 3 caracteres)
                  />
                  {errors.cpf && <p className="text-red-500 text-sm mt-1">{errors.cpf}</p>}
                  {errors.cnpj && <p className="text-red-500 text-sm mt-1">{errors.cnpj}</p>}
                </div>
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">Telefone *</label>
                  <input type="text" value={contrato.telefone || ''} onChange={(e) => setContrato(prev => ({ ...prev, telefone: e.target.value }))} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${errors.telefone ? 'border-red-500' : 'border-gray-300'}`} placeholder="Telefone *"/>
                  {errors.telefone && <p className="text-red-500 text-sm mt-1">{errors.telefone}</p>}
                </div>
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input type="email" value={contrato.email || ''} onChange={(e) => setContrato(prev => ({ ...prev, email: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="Email"/>
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2"><Calendar className="w-6 h-6 text-primary" />Dados da Festa</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">Nome do Aniversariante *</label>
                  <input type="text" value={contrato.nomeAniversariante || ''} onChange={(e) => setContrato(prev => ({ ...prev, nomeAniversariante: e.target.value }))} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${errors.nomeAniversariante ? 'border-red-500' : 'border-gray-300'}`} placeholder="Nome do Aniversariante *"/>
                  {errors.nomeAniversariante && <p className="text-red-500 text-sm mt-1">{errors.nomeAniversariante}</p>}
                </div>
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">Idade *</label>
                  <input type="number" value={contrato.idadeAniversariante || ''} onChange={(e) => setContrato(prev => ({ ...prev, idadeAniversariante: parseInt(e.target.value) }))} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${errors.idadeAniversariante ? 'border-red-500' : 'border-gray-300'}`} placeholder="Idade *" min="1" max="100"/>
                  {errors.idadeAniversariante && <p className="text-red-500 text-sm mt-1">{errors.idadeAniversariante}</p>}
                </div>
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">Data da Festa *</label>
                  <input type="date" value={contrato.dataFesta || ''} onChange={(e) => setContrato(prev => ({ ...prev, dataFesta: e.target.value }))} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${errors.dataFesta ? 'border-red-500' : 'border-gray-300'}`}/>
                  {errors.dataFesta && <p className="text-red-500 text-sm mt-1">{errors.dataFesta}</p>}
                </div>
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">Horário de Início *</label>
                  <input type="time" value={contrato.horarioInicioFesta || ''} onChange={(e) => setContrato(prev => ({ ...prev, horarioInicioFesta: e.target.value }))} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${errors.horarioInicioFesta ? 'border-red-500' : 'border-gray-300'}`}/>
                  {errors.horarioInicioFesta && <p className="text-red-500 text-sm mt-1">{errors.horarioInicioFesta}</p>}
                </div>
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">Tema da Festa</label>
                  <input type="text" value={contrato.temaFesta || ''} onChange={(e) => setContrato(prev => ({ ...prev, temaFesta: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="Tema da Festa"/>
                </div>
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">Número de Convidados</label>
                  <input type="number" value={contrato.numeroConvidados || ''} onChange={(e) => setContrato(prev => ({ ...prev, numeroConvidados: parseInt(e.target.value) }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="Número de Convidados" min="1"/>
                </div>
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">Horário de Início da Recreação *</label>
                  <input type="time" value={contrato.inicioRecreacao || ''} onChange={(e) => setContrato(prev => ({ ...prev, inicioRecreacao: e.target.value }))} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${errors.inicioRecreacao ? 'border-red-500' : 'border-gray-300'}`}/>
                  {errors.inicioRecreacao && <p className="text-red-500 text-sm mt-1">{errors.inicioRecreacao}</p>}
                </div>
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">Horário de Fim da Recreação *</label>
                  <input type="time" value={contrato.fimRecreacao || ''} onChange={(e) => setContrato(prev => ({ ...prev, fimRecreacao: e.target.value }))} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${errors.fimRecreacao ? 'border-red-500' : 'border-gray-300'}`}/>
                  {errors.fimRecreacao && <p className="text-red-500 text-sm mt-1">{errors.fimRecreacao}</p>}
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2"><MapPin className="w-6 h-6 text-primary" />Endereço da Festa</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">CEP</label>
                  <input type="text" value={contrato.cep || ''} onChange={(e) => { const cep = e.target.value.replace(/\D/g, ''); setContrato(prev => ({ ...prev, cep })); if (cep.length === 8) { buscarCEP(cep); } }} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="CEP" maxLength={9}/>
                </div>
                <div className="md:col-span-2">
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">Endereço *</label>
                  <input type="text" value={contrato.endereco || ''} onChange={(e) => setContrato(prev => ({ ...prev, endereco: e.target.value }))} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${errors.endereco ? 'border-red-500' : 'border-gray-300'}`} placeholder="Endereço *"/>
                  {errors.endereco && <p className="text-red-500 text-sm mt-1">{errors.endereco}</p>}
                </div>
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">Número *</label>
                  <input type="text" value={contrato.numero || ''} onChange={(e) => setContrato(prev => ({ ...prev, numero: e.target.value }))} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${errors.numero ? 'border-red-500' : 'border-gray-300'}`} placeholder="Número *"/>
                  {errors.numero && <p className="text-red-500 text-sm mt-1">{errors.numero}</p>}
                </div>
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">Complemento</label>
                  <input type="text" value={contrato.complemento || ''} onChange={(e) => setContrato(prev => ({ ...prev, complemento: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="Complemento"/>
                </div>
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">Bairro *</label>
                  <input type="text" value={contrato.bairro || ''} onChange={(e) => setContrato(prev => ({ ...prev, bairro: e.target.value }))} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${errors.bairro ? 'border-red-500' : 'border-gray-300'}`} placeholder="Bairro *"/>
                  {errors.bairro && <p className="text-red-500 text-sm mt-1">{errors.bairro}</p>}
                </div>
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">Cidade *</label>
                  <input type="text" value={contrato.cidade || ''} onChange={(e) => setContrato(prev => ({ ...prev, cidade: e.target.value }))} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${errors.cidade ? 'border-red-500' : 'border-gray-300'}`} placeholder="Cidade *"/>
                  {errors.cidade && <p className="text-red-500 text-sm mt-1">{errors.cidade}</p>}
                </div>
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">Estado *</label>
                  <select value={contrato.estado || ''} onChange={(e) => setContrato(prev => ({ ...prev, estado: e.target.value }))} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${errors.estado ? 'border-red-500' : 'border-gray-300'}`}>
                    <option value="">Selecione o Estado *</option>
                    <option value="AC">Acre</option><option value="AL">Alagoas</option><option value="AP">Amapá</option><option value="AM">Amazonas</option><option value="BA">Bahia</option><option value="CE">Ceará</option><option value="DF">Distrito Federal</option><option value="ES">Espírito Santo</option><option value="GO">Goiás</option><option value="MA">Maranhão</option><option value="MT">Mato Grosso</option><option value="MS">Mato Grosso do Sul</option><option value="MG">Minas Gerais</option><option value="PA">Pará</option><option value="PB">Paraíba</option><option value="PR">Paraná</option><option value="PE">Pernambuco</option><option value="PI">Piauí</option><option value="RJ">Rio de Janeiro</option><option value="RN">Rio Grande do Norte</option><option value="RS">Rio Grande do Sul</option><option value="RO">Rondônia</option><option value="RR">Roraima</option><option value="SC">Santa Catarina</option><option value="SP">São Paulo</option><option value="SE">Sergipe</option><option value="TO">Tocantins</option>
                  </select>
                  {errors.estado && <p className="text-red-500 text-sm mt-1">{errors.estado}</p>}
                </div>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2"><Check className="w-6 h-6 text-primary" />Revisão dos Dados</h2>
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Dados do Contratante</h3>
                    <p><strong>Nome:</strong> {contrato.contratante}</p>
                    <p><strong>{getDocumentLabel(contrato.tipoDocumento || 'cpf')}:</strong> {contrato.tipoDocumento === 'cnpj' ? contrato.cnpj : contrato.cpf}</p>
                    <p><strong>Telefone:</strong> {contrato.telefone}</p>
                    <p><strong>Email:</strong> {contrato.email || 'Não informado'}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Dados da Festa</h3>
                    <p><strong>Aniversariante:</strong> {contrato.nomeAniversariante}</p>
                    <p><strong>Idade:</strong> {contrato.idadeAniversariante} anos</p>
                    <p><strong>Data:</strong> {contrato.dataFesta ? formatDate(contrato.dataFesta) : 'Não informada'}</p>
                    <p><strong>Horário:</strong> {contrato.horarioInicioFesta}</p>
                    <p><strong>Tema:</strong> {contrato.temaFesta || 'Não informado'}</p>
                    <p><strong>Convidados:</strong> {contrato.numeroConvidados || 'Não informado'}</p>
                    <p><strong>Início da Recreação:</strong> {contrato.inicioRecreacao || 'Não informado'}</p>
                    <p><strong>Fim da Recreação:</strong> {contrato.fimRecreacao || 'Não informado'}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Endereço</h3>
                    <p><strong>Endereço:</strong> {contrato.endereco}, {contrato.numero}</p>
                    <p><strong>Complemento:</strong> {contrato.complemento || 'Não informado'}</p>
                    <p><strong>Bairro:</strong> {contrato.bairro}</p>
                    <p><strong>Cidade:</strong> {contrato.cidade} - {contrato.estado}</p>
                    <p><strong>CEP:</strong> {contrato.cep || 'Não informado'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between mt-8">
            <button onClick={prevStep} disabled={currentStep === 1} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Anterior</span>
            </button>
            {currentStep < 4 ? (
              <button onClick={nextStep} className="flex items-center gap-2 px-6 py-3 text-lg font-semibold bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transform transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                <span className="hidden sm:inline">Próximo</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={loading} className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors">
                {loading ? (
                  <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>Enviando...</>
                ) : (
                  <><Check className="w-5 h-5" />Enviar Solicitação</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Formulario;
